import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Package, TrendingUp, AlertCircle, Clock, AlertTriangle } from "lucide-react";
import { hasRedYields } from "@/utils/productionCalculations";
import "./WorkOrderCard.css";

const WorkOrderCard = ({ workOrder, onClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: workOrder.wo_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
    zIndex: isDragging ? 1000 : 'auto',
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "scheduled":
        return "bg-amber-100 text-amber-800 border-amber-300";
      case "in_progress":
        return "bg-blue-100 text-blue-800 border-blue-300";
      case "completed":
        return "bg-green-100 text-green-800 border-green-300";
      case "cancelled":
        return "bg-red-100 text-red-800 border-red-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };

  const getLineColor = (line) => {
    switch (line) {
      case "P1":
        return "line-p1";
      case "P2":
        return "line-p2";
      case "P3":
        return "line-p3";
      default:
        return "line-default";
    }
  };

  const handleClick = (e) => {
    e.stopPropagation(); // Prevent day column click
    onClick(workOrder);
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`work-order-card ${getLineColor(workOrder.production_line)}`}
      onClick={handleClick}
      data-testid={`work-order-card-${workOrder.wo_id}`}
    >
      <CardContent className="work-order-content flex flex-col h-full">
        <div className="work-order-header flex justify-between items-start mb-2">
          <div className="flex flex-col">
            <span className="wo-number font-bold text-xs" data-testid="wo-number">{workOrder.wo_number}</span>
            {workOrder.customer_po && (
              <span className="text-[10px] text-blue-600 font-medium" title="Customer PO">
                PO: {workOrder.customer_po}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
            {workOrder.bom_usage && hasRedYields(workOrder.bom_usage) && (
              <AlertTriangle className="w-4 h-4 text-red-600" title="Yield Alert" data-testid="yield-alert" />
            )}
            <Badge className={`status-badge text-[10px] px-1.5 py-0.5 ${getStatusColor(workOrder.status)}`} data-testid="wo-status">
              {workOrder.status.replace("_", " ")}
            </Badge>
          </div>
        </div>
        
        {/* Updated Item Name Section: Allows wrapping */}
        <div className="work-order-item flex items-start gap-1.5 mb-3">
          <Package className="w-3.5 h-3.5 text-gray-600 flex-shrink-0 mt-0.5" />
          <span 
            className="item-name font-semibold text-gray-900 text-sm leading-snug whitespace-normal" 
            data-testid="wo-item-name"
            title={workOrder.item_name}
          >
            {workOrder.item_name || "No Item Name"}
          </span>
        </div>
        
        {/* Updated Stats Section: Time on Left, Qty on Bottom Right */}
        <div className="work-order-stats flex justify-between items-end mt-auto pt-2 border-t border-gray-100">
          <div className="stat-item flex items-center gap-1">
            <Clock className="w-3 h-3 text-indigo-600 flex-shrink-0" />
            <span className="stat-text text-xs text-gray-600" data-testid="wo-time">
              {workOrder.planned_start_time} - {workOrder.planned_end_time}
            </span>
          </div>
          
          <div className="flex flex-col items-end gap-0.5">
            <div className="stat-item flex items-center gap-1">
              <TrendingUp className="w-3 h-3 text-green-600 flex-shrink-0" />
              <span className="stat-text text-xs font-medium" data-testid="wo-planned-qty">
                Qty: {workOrder.planned_quantity}
              </span>
            </div>
            {workOrder.actual_quantity > 0 && (
              <div className="stat-item flex items-center gap-1">
                <AlertCircle className="w-3 h-3 text-blue-600 flex-shrink-0" />
                <span className="stat-text text-xs font-medium text-blue-700" data-testid="wo-actual-qty">
                  Act: {workOrder.actual_quantity}
                </span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default WorkOrderCard;